import { InjectionToken } from '@angular/core';

import { ModelSetTransformer, ModelTransformer } from '../common/common.model';

export interface ExtGetTranslationsRequest {
    languageID: number;
    translationIDs: string;
    tenantID?: number;
}

export interface ExtLanguage {
    languageId: number;
    name: string;
    initials: string;
    textId: number;
    initialsTextId: number;
}

export interface Language {
    id: number;
    name: string;
    initial: string;
    textId: number;
    initialTextId: number;
}

export const LOCALES = ['en', 'en-CA', 'fr', 'fr-CA', 'de', 'es'];

export const DEFAULT_LOCALE = LOCALES[0];

export const DEFAULT_LANGUAGE: Language = {
    id: 1,
    name: null,
    initial: DEFAULT_LOCALE,
    textId: null,
    initialTextId: null,
};

class LanguageModelTransformer
    implements ModelTransformer<Language, ExtLanguage>
{
    transform(model: ExtLanguage): Language {
        return {
            id: model.languageId,
            name: model.name,
            initial: model.initials,
            textId: model.textId,
            initialTextId: model.initialsTextId,
        };
    }
}

export class LanguageModelSetTransformer extends ModelSetTransformer<
    Language,
    ExtLanguage
> {
    protected newTransformer(): ModelTransformer<Language, ExtLanguage> {
        return new LanguageModelTransformer();
    }

    transform(model: ExtLanguage[]): Language[] {
        const transformed = super.transform(model).filter((e) => !!e.initial);

        // clean
        transformed.forEach((e) => {
            const langRegion = e.initial.split('-', 2);
            langRegion[0] = langRegion[0].toLowerCase();
            if (langRegion.length === 2) {
                if (!langRegion[1]) {
                    langRegion.pop();
                } else {
                    langRegion[1] = langRegion[1].toUpperCase();
                }
            }
            e.initial = langRegion.join('-');
        });

        return transformed;
    }
}

export interface I18nIdMap {
    [key: string]:
        | {
              textId: number | null;
              defaultText: string | null;
          }
        | undefined;
}

export interface I18nKeyMap {
    [key: string]: string;
}

export const CMPS_I18N_MAP = new InjectionToken<string[]>('CMPS_I18N_MAP');

export interface ExtTranslation {
    id: string;
    tenantID: number;
    languageID: number;
    textID: number;
    text: string;
}

export interface Translation {
    id: number;
    text: string;
}

class TranslationModelTransformer
    implements ModelTransformer<Translation, ExtTranslation>
{
    transform(model: ExtTranslation): Translation {
        return {
            id: model.textID,
            text: model.text,
        };
    }
}

export class TranslationModelSetTransformer extends ModelSetTransformer<
    Translation,
    ExtTranslation
> {
    protected newTransformer(): ModelTransformer<Translation, ExtTranslation> {
        return new TranslationModelTransformer();
    }
}

export class NgxTranslationModelTransformer
    implements ModelTransformer<any, Translation[]>
{
    constructor(private readonly _map: I18nIdMap) {}

    transform(model: Translation[]): any {
        const transformed = {};
        Object.keys(this._map).forEach((key) => {
            // eslint-disable-next-line no-useless-escape
            const path = key.split(/(?!^\.)\.[^\.|\s]/);
            let loc = transformed;
            while (path.length > 1) {
                const k = path.shift();
                loc[k] = {};
                loc = loc[k];
            }
            const locKey = path.shift();
            const tl = model.find(
                (value) => value.id === this._map[key]?.textId,
            );
            if (tl) {
                // TODO revise
                loc[locKey] = tl.text
                    .replace(/(^|[^{]){([^{])/g, '$1{{$2')
                    .replace(/([^}])}([^}]|$)/g, '$1}}$2');
            } else {
                loc[locKey] = this._map[key]?.defaultText;
            }
        });
        return transformed;
    }
}

function appHasLocale(locale: string): boolean {
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    if (LOCALES.indexOf(locale) !== -1) return true;

    const lang = locale.split('-', 1)[0];
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    return LOCALES.indexOf(lang) !== -1;
}

function findRemoteLocale(locales: Language[], locale: string): Language {
    const found = locales.find((e) => e.initial === locale);
    if (found) return found;

    const langs = locales.map((e) => e.initial.split('-', 1)[0]);
    const lang = locale.split('-', 1)[0];
    const index = langs.indexOf(lang);
    if (index !== -1) {
        const result = locales[index];
        // enforce matched
        result.initial = lang;
        return result;
    }
    return null;
}

export function determineDefaultLocale(
    locale: string,
    languages: Language[],
): { locale: string; lang: string } {
    const remoteLocales = languages.filter((e) => appHasLocale(e.initial)); // reduce

    let foundLocale = findRemoteLocale(remoteLocales, locale); // if remote supports requested locale
    if (!foundLocale) {
        foundLocale = remoteLocales.find((e) => e.initial === DEFAULT_LOCALE); // if remote support default app locale
    }

    foundLocale = foundLocale || DEFAULT_LANGUAGE;

    return { locale: foundLocale.initial, lang: foundLocale.id.toString() };
}
